import React, { useState } from 'react';
import { Box, Typography, Paper, Grid, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useAgentsStatsOverviewQuery } from '../../API/Agents';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import dayjs from 'dayjs';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const AgentAnalytics = () => {
  const [days, setDays] = useState(30);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract userId and userName from URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const userId = searchParams.get('userId');
  const userName = searchParams.get('userName');
  
  const { data: statsData, isLoading, error } = useAgentsStatsOverviewQuery({ 
    days,
    userId: userId 
  });
  const stats = statsData?.data || {};

  const handleDaysChange = (event: any) => {
    setDays(event.target.value);
  };

  // Prepare data for charts
  const creationTrendData = stats.agentsTrend ? stats.agentsTrend.map((trend: any) => ({
    date: dayjs(trend.date).format('MMM DD'),
    fullDate: trend.date,
    agents: parseInt(trend.count)
  })) : [];

  // Prepare call statistics
  const callStats = stats.callStats || {};
  const costTrendData = callStats.costTrend ? callStats.costTrend.map((trend: any) => ({
    date: dayjs(trend.date).format('MMM DD'),
    fullDate: trend.date,
    cost: parseFloat(trend.cost),
    calls: parseInt(trend.calls)
  })) : [];

  // Helper function to format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount);
  };

  // Helper function to format duration (seconds to minutes:seconds)
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className="main-dashboard-div">
        <div className="dashboard-wrapper">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Typography>Loading agent analytics...</Typography>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-dashboard-div">
        <div className="dashboard-wrapper">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Typography color="error">Error loading analytics data</Typography>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-dashboard-div">
      <div className="dashboard-wrapper">
        <div className="heading-dashboard">
          {userId && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div
                onClick={() => navigate('/app/users')}
                style={{
                  cursor: 'pointer',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  backgroundColor: 'transparent',
                  border: '1px solid #d1d5db',
                  color: '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                }}
                onMouseEnter={(e) => {
                  const target = e.target as HTMLElement;
                  target.style.backgroundColor = '#f9fafb';
                  target.style.borderColor = '#9ca3af';
                  target.style.transform = 'translateY(-1px)';
                  target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  const target = e.target as HTMLElement;
                  target.style.backgroundColor = 'transparent';
                  target.style.borderColor = '#d1d5db';
                  target.style.transform = 'translateY(0)';
                  target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                }}
                title="Back to Users"
              >
                <ArrowBack fontSize="small" />
                <span>Back to Users</span>
              </div>
              <div style={{
                padding: '6px 12px',
                backgroundColor: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '6px',
                color: '#92400e',
                fontSize: '13px',
                fontWeight: '500'
              }}>
                Analytics for: {userName ? decodeURIComponent(userName) : `User ID: ${userId}`}
              </div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>Agent Analytics Overview</h2>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={days}
              label="Time Period"
              onChange={handleDaysChange}
            >
              <MenuItem value={7}>Last 7 Days</MenuItem>
              <MenuItem value={30}>Last 30 Days</MenuItem>
              <MenuItem value={90}>Last 90 Days</MenuItem>
            </Select>
          </FormControl>
        </div>

        <Grid container spacing={3}>
          {/* Call Statistics Section */}
          {callStats && Object.keys(callStats).length > 0 && (
            <>
              <Grid item xs={12}>
                <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
                  📞 Call Statistics
                </Typography>
              </Grid>
              
              {/* Call Summary Cards */}
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#e3f2fd' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                    {callStats.totalCalls || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Calls
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#e8f5e8' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                    {callStats.successfulCalls || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Successful Calls
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#ffebee' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                    {callStats.failedCalls || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Failed Calls
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#fff3e0' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                    {callStats.inProgressCalls || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    In Progress
                  </Typography>
                </Paper>
              </Grid>

              {/* Call Metrics Cards */}
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f3e5f5' }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#7b1fa2' }}>
                    {formatDuration(callStats.totalDuration || 0)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Duration
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#e0f2f1' }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#00695c' }}>
                    {formatDuration(callStats.averageDuration || 0)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Average Duration
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#fce4ec' }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#c2185b' }}>
                    {formatCurrency(callStats.totalCost || 0)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Cost
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#e8eaf6' }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#3f51b5' }}>
                    {formatCurrency(callStats.averageCost || 0)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Average Cost
                  </Typography>
                </Paper>
              </Grid>

              {/* Cost Trend Chart */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                    💰 Cost & Calls Trend
                  </Typography>
                  {costTrendData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={costTrendData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis yAxisId="cost" orientation="left" />
                        <YAxis yAxisId="calls" orientation="right" />
                        <Tooltip 
                          formatter={(value, name) => [
                            name === 'Cost' ? formatCurrency(Number(value)) : value,
                            name
                          ]}
                        />
                        <Legend />
                        <Line 
                          yAxisId="cost"
                          type="monotone" 
                          dataKey="cost" 
                          stroke="#c2185b" 
                          strokeWidth={2}
                          dot={{ fill: '#c2185b' }}
                          name="Cost"
                        />
                        <Line 
                          yAxisId="calls"
                          type="monotone" 
                          dataKey="calls" 
                          stroke="#1976d2" 
                          strokeWidth={2}
                          dot={{ fill: '#1976d2' }}
                          name="Calls"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography color="textSecondary">No cost trend data available</Typography>
                  )}
                </Paper>
              </Grid>

              {/* Call Breakdown */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                    📊 Calls by Status
                  </Typography>
                  {callStats.callsByStatus && callStats.callsByStatus.length > 0 ? (
                    <Grid container spacing={2}>
                      {callStats.callsByStatus.map((statusData: any, index: number) => (
                        <Grid item xs={6} key={index}>
                          <Box sx={{ 
                            textAlign: 'center', 
                            p: 2, 
                            backgroundColor: statusData.status === 'ended' ? '#e8f5e8' : '#ffebee', 
                            borderRadius: 2,
                            border: `2px solid ${statusData.status === 'ended' ? '#4caf50' : '#f44336'}`
                          }}>
                            <Typography variant="h5" sx={{ 
                              fontWeight: 'bold', 
                              color: statusData.status === 'ended' ? '#2e7d32' : '#d32f2f' 
                            }}>
                              {statusData.count}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ textTransform: 'capitalize' }}>
                              {statusData.status}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography color="textSecondary">No status breakdown available</Typography>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                    📱 Calls by Type
                  </Typography>
                  {callStats.callsByType && callStats.callsByType.length > 0 ? (
                    <Box>
                      {callStats.callsByType.map((typeData: any, index: number) => (
                        <Box key={index} sx={{ 
                          mb: 2, 
                          p: 2, 
                          backgroundColor: '#f8f9fa', 
                          borderRadius: 2,
                          border: '1px solid #e9ecef'
                        }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, textTransform: 'capitalize' }}>
                            {typeData.callType.replace(/([A-Z])/g, ' $1').trim()}
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={4}>
                              <Typography variant="body2" color="textSecondary">Calls</Typography>
                              <Typography variant="h6">{typeData.count}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2" color="textSecondary">Duration</Typography>
                              <Typography variant="h6">{formatDuration(parseInt(typeData.totalDuration))}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2" color="textSecondary">Cost</Typography>
                              <Typography variant="h6">{formatCurrency(parseFloat(typeData.totalCost))}</Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography color="textSecondary">No type breakdown available</Typography>
                  )}
                </Paper>
              </Grid>

              {/* Agent Statistics Section Header */}
              <Grid item xs={12}>
                <Typography variant="h5" sx={{ mb: 2, mt: 4, color: 'primary.main', fontWeight: 'bold' }}>
                  🤖 Agent Statistics
                </Typography>
              </Grid>
            </>
          )}

          {/* Agent Summary Cards */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#e3f2fd' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                {stats.totalAgents || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Agents
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#e8f5e8' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                {stats.activeAgents || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Active Agents
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#ffebee' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                {stats.deletedAgents || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Deleted Agents
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#fff3e0' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                {stats.activeAgents && stats.totalAgents ? 
                  Math.round((stats.activeAgents / stats.totalAgents) * 100) : 0}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Active Rate
              </Typography>
            </Paper>
          </Grid>



          {/* Agent Creation Trend - Line Chart */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                Agent Creation Trend
              </Typography>
              {creationTrendData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={creationTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="agents" 
                      stroke="#1976d2" 
                      strokeWidth={2}
                      dot={{ fill: '#1976d2' }}
                      name="Agents Created"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Typography color="textSecondary">No trend data available</Typography>
              )}
            </Paper>
          </Grid>


          {/* Summary Stats */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                Summary Statistics (Last {days} Days)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="textSecondary">Total Providers</Typography>
                    <Typography variant="h6">
                      {(stats.agentsByProvider?.length || 0) + (stats.agentsByVoiceProvider?.length || 0)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="textSecondary">Active Users</Typography>
                    <Typography variant="h6">{stats.agentsByUser?.length || 0}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="textSecondary">Creation Days</Typography>
                    <Typography variant="h6">{stats.agentsTrend?.length || 0}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="textSecondary">Avg per Day</Typography>
                    <Typography variant="h6">
                      {stats.agentsTrend && stats.agentsTrend.length > 0 ? 
                        Math.round(stats.agentsTrend.reduce((sum: number, trend: any) => sum + parseInt(trend.count), 0) / stats.agentsTrend.length) 
                        : 0}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default AgentAnalytics;